import { getScopedDomain } from '../lib/tenantScope';
// Firmware History — per-device + per-vendor view of phone firmware versions
// observed by the PnP / provisioning pipeline. Surfaces the at-risk join with
// firmware_advisories so admins can see which phones need updating.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cpu, AlertTriangle, Clock } from 'lucide-react';
import { api } from '../lib/api';

export default function FirmwareHistory() {
  const me = api.me();
  const [tab, setTab] = useState<'recent' | 'at-risk' | 'by-vendor'>('at-risk');

  const recent = useQuery({
    queryKey: ['fw-recent', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.firmwareHistory.list({ domain_uuid: getScopedDomain(me?.domain_uuid), limit: 100 }) : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid) && tab === 'recent',
  });

  const atRisk = useQuery({
    queryKey: ['fw-at-risk', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.firmwareHistory.atRisk({ domain_uuid: getScopedDomain(me?.domain_uuid), limit: 100 }) : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid) && tab === 'at-risk',
  });

  const byVendor = useQuery({
    queryKey: ['fw-by-vendor', getScopedDomain(me?.domain_uuid)],
    queryFn: () => getScopedDomain(me?.domain_uuid) ? api.firmwareHistory.byVendor(getScopedDomain(me?.domain_uuid)) : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid) && tab === 'by-vendor',
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Cpu /> Firmware History</h1>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(['at-risk', 'recent', 'by-vendor'] as const).map(v => (
          <button key={v} onClick={() => setTab(v)}
            className={`px-4 py-2 text-sm capitalize ${tab === v ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}>
            {v.replace('-', ' ')}
          </button>
        ))}
      </div>

      {tab === 'at-risk' && (
        <div className="space-y-3">
          <div className="card p-3 text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="text-warning mt-0.5 shrink-0" />
            <div>Devices running firmware versions flagged in <code>firmware_advisories</code>. Severity ranks: critical &gt; high &gt; medium &gt; low. Push a reboot to a device from the Phones page after upgrading.</div>
          </div>
          <div className="card p-0 overflow-hidden">
            <table className="table">
              <thead><tr><th>Severity</th><th>Vendor / Model</th><th>Current</th><th>Advised</th><th>MAC</th><th>Last seen</th></tr></thead>
              <tbody>
                {(atRisk.data || []).map((r: any, i: number) => (
                  <tr key={i} className="hover:bg-elevated">
                    <td><SeverityBadge s={r.severity} /></td>
                    <td>{r.vendor_key} / {r.model || '—'}</td>
                    <td className="font-mono">{r.firmware}</td>
                    <td className="font-mono text-success">{r.advised_version || '—'}</td>
                    <td className="font-mono text-xs">{r.mac_address}</td>
                    <td className="text-xs">{r.seen_at ? new Date(r.seen_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
                {!atRisk.data?.length && <tr><td colSpan={6} className="text-center py-6 text-textSecondary">No at-risk devices.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'recent' && (
        <div className="card p-0 overflow-hidden">
          <table className="table">
            <thead><tr><th>When</th><th>MAC</th><th>Vendor / Model</th><th>Firmware</th><th>Source</th></tr></thead>
            <tbody>
              {(recent.data || []).map((r: any) => (
                <tr key={r.history_id} className="hover:bg-elevated">
                  <td className="text-xs"><Clock size={12} className="inline mr-1" />{new Date(r.seen_at).toLocaleString()}</td>
                  <td className="font-mono text-xs">{r.mac_address}</td>
                  <td>{r.vendor_key || '—'} / {r.model || '—'}</td>
                  <td className="font-mono">{r.firmware}</td>
                  <td><span className="badge">{r.source}</span></td>
                </tr>
              ))}
              {!recent.data?.length && <tr><td colSpan={5} className="text-center py-6 text-textSecondary">No firmware events.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'by-vendor' && (
        <div className="card p-0 overflow-hidden">
          <table className="table">
            <thead><tr><th>Vendor</th><th>Model</th><th>Firmware</th><th>Devices</th><th>Most recent</th></tr></thead>
            <tbody>
              {(byVendor.data || []).map((r: any, i: number) => (
                <tr key={i} className="hover:bg-elevated">
                  <td>{r.vendor_key || '—'}</td>
                  <td>{r.model || '—'}</td>
                  <td className="font-mono">{r.firmware}</td>
                  <td>{r.device_count}</td>
                  <td className="text-xs">{r.most_recent ? new Date(r.most_recent).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
              {!byVendor.data?.length && <tr><td colSpan={5} className="text-center py-6 text-textSecondary">No firmware events.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SeverityBadge({ s }: { s: string }) {
  const cls = s === 'critical' ? 'bg-danger/20 text-danger'
            : s === 'high'     ? 'bg-warning/20 text-warning'
            : s === 'medium'   ? 'bg-primary/20 text-primary'
            : 'bg-textSecondary/20 text-textSecondary';
  return <span className={`badge ${cls}`}>{s}</span>;
}
